/**
 * useGlobeMap - Public hook for consuming globe/map context.
 *
 * This is the primary hook for components to access globe/map state and operations.
 * It provides a unified API for both 3D globe and 2D map renderers.
 */

import { useContext } from 'react'
import { GlobeMapContext } from '../context/GlobeMapContext'
import type { GlobeMapContextValue } from '../types'

/**
 * Hook to access globe/map state and operations.
 *
 * Must be used within a GlobeMapProvider.
 *
 * @example
 * ```tsx
 * function MapControls() {
 *   const { zoom, setZoom, panTo, mode } = useGlobeMap()
 *
 *   return (
 *     <div>
 *       <button onClick={() => setZoom(zoom + 1)}>Zoom In</button>
 *       <button onClick={() => panTo(48.8566, 2.3522)}>Go to Paris</button>
 *       <span>Mode: {mode}</span>
 *     </div>
 *   )
 * }
 * ```
 *
 * @returns Globe/map context value with state and operations
 * @throws Error if used outside of GlobeMapProvider
 */
export function useGlobeMap(): GlobeMapContextValue {
  const context = useContext(GlobeMapContext)

  if (context === null) {
    throw new Error(
      'useGlobeMap must be used within a GlobeMapProvider. ' +
        'Wrap your component tree with <GlobeMapProvider>.'
    )
  }

  return context
}

/**
 * Hook to access only the view state (read-only).
 *
 * Useful when you only need to read state without operations.
 *
 * @example
 * ```tsx
 * function PositionDisplay() {
 *   const { center, zoom } = useGlobeMapState()
 *   return <span>{center.latitude}, {center.longitude} @ z{zoom}</span>
 * }
 * ```
 */
export function useGlobeMapState() {
  const {
    center,
    zoom,
    heading,
    mode,
    selectedTripId,
    markers,
    curvature,
    animating,
    fov,
    targetCurvature,
    visibleTiles,
  } = useGlobeMap()

  return {
    center,
    zoom,
    heading,
    mode,
    selectedTripId,
    markers,
    curvature,
    animating,
    fov,
    targetCurvature,
    visibleTiles,
  }
}

/**
 * Hook to access only the operations (actions).
 *
 * Useful when you only need to dispatch actions without reading state.
 *
 * @example
 * ```tsx
 * function SearchResultItem({ result }) {
 *   const { panTo, selectTrip } = useGlobeMapActions()
 *   return (
 *     <button onClick={() => {
 *       panTo(result.lat, result.lng)
 *       selectTrip(result.tripId)
 *     }}>
 *       {result.name}
 *     </button>
 *   )
 * }
 * ```
 */
export function useGlobeMapActions() {
  const {
    panTo,
    setZoom,
    setHeading,
    selectTrip,
    deselectTrip,
    addMarker,
    removeMarker,
    setMarkers,
    fitBounds,
  } = useGlobeMap()

  return {
    panTo,
    setZoom,
    setHeading,
    selectTrip,
    deselectTrip,
    addMarker,
    removeMarker,
    setMarkers,
    fitBounds,
  }
}
